const PHONE_SHADOW_EMAIL_PREFIX = 'sms-';
const PHONE_SHADOW_EMAIL_DOMAIN = '@customer-login.invalid';

/**
 * Org-scoped shadow credential shape (GitLab #804, P2.1):
 * `c-{org uuid, no hyphens}-{24 hex}@customer-login.invalid`. Built
 * server-side by `lib/referral/orgShadowCredential.ts`; detection lives here
 * so client components can recognise it without pulling in `node:crypto`.
 */
export const ORG_SHADOW_EMAIL_PREFIX = 'c-';
export const SHADOW_EMAIL_DOMAIN = PHONE_SHADOW_EMAIL_DOMAIN;
const ORG_SHADOW_EMAIL_PATTERN = /^c-[0-9a-f]{32}-[0-9a-f]{24}@customer-login\.invalid$/;

export function isOrgShadowEmail(email?: string | null): boolean {
  if (!email) return false;
  return ORG_SHADOW_EMAIL_PATTERN.test(email.trim().toLowerCase());
}

/**
 * Display-safe email for an auth user. Org-scoped accounts sign in with an
 * internal `c-…@customer-login.invalid` credential, so the real address is
 * read from `user_metadata.contact_email` instead (null for phone-only
 * accounts). Any other email is returned unchanged.
 */
export function resolveOrgShadowDisplayEmail(
  email?: string | null,
  userMetadata?: Record<string, unknown> | null,
): string | null {
  if (!isOrgShadowEmail(email)) return email ?? null;
  const contactEmail = userMetadata?.contact_email;
  return typeof contactEmail === 'string' && contactEmail.trim()
    ? contactEmail.trim()
    : null;
}

export function buildPhoneShadowEmail(phone: string): string {
  const digits = phone.replace(/[^0-9]/g, '');
  return `${PHONE_SHADOW_EMAIL_PREFIX}${digits}${PHONE_SHADOW_EMAIL_DOMAIN}`;
}

export function isPhoneShadowEmail(email?: string | null): boolean {
  if (!email) return false;
  const trimmed = email.trim().toLowerCase();
  return trimmed.startsWith(PHONE_SHADOW_EMAIL_PREFIX)
    && trimmed.endsWith(PHONE_SHADOW_EMAIL_DOMAIN);
}

export function resolvePhoneLoginContact(
  email?: string | null,
  phone?: string | null,
): string | null {
  const trimmedEmail = typeof email === 'string' ? email.trim() : '';
  const trimmedPhone = typeof phone === 'string' ? phone.trim() : '';

  if (isPhoneShadowEmail(trimmedEmail) && trimmedPhone) {
    return trimmedPhone;
  }

  return trimmedEmail || null;
}

/**
 * Recovers the E.164 phone number encoded in a phone-login shadow email,
 * e.g. `sms-447909282076@customer-login.invalid` → `+447909282076`.
 * Returns null when the input is not a shadow email or has no digits.
 */
export function extractPhoneFromShadowEmail(email?: string | null): string | null {
  if (!isPhoneShadowEmail(email)) return null;
  const trimmed = (email as string).trim().toLowerCase();
  const digits = trimmed
    .slice(PHONE_SHADOW_EMAIL_PREFIX.length, trimmed.length - PHONE_SHADOW_EMAIL_DOMAIN.length)
    .replace(/[^0-9]/g, '');
  return digits ? `+${digits}` : null;
}

/**
 * Human-facing identity for a customer: the real email when present, otherwise
 * the phone number for phone-login accounts. Prefers an explicit phone value,
 * falling back to the digits encoded in the shadow email so the raw
 * `sms-…@customer-login.invalid` address is never shown to the user.
 */
export function resolvePhoneLoginDisplay(
  email?: string | null,
  phone?: string | null,
): string | null {
  if (isPhoneShadowEmail(email)) {
    const trimmedPhone = typeof phone === 'string' ? phone.trim() : '';
    return trimmedPhone || extractPhoneFromShadowEmail(email);
  }
  const trimmedEmail = typeof email === 'string' ? email.trim() : '';
  return trimmedEmail || null;
}